import { STEPPER_GLOBAL_OPTIONS } from '@angular/cdk/stepper';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, Signal, signal } from '@angular/core';
import { FavoriteViewSubscribersFormComponent } from '../subscribers-form/subscribers-form.component';
import { ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatStepperModule } from '@angular/material/stepper';
import { FavoriteView, FavoriteViewReportStatus, FeatureFlags, Filter } from '@iot-platform/models/common';
import { I4BGrid, I4BGridData, I4BGridOptions } from '@iot-platform/models/grid-engine';
import { FeatureFlagsService, MediaWatcherService } from '@iot-platform/core';
import { TranslatePipe } from '@ngx-translate/core';
import { FavoriteViewFormSteps } from '../../../models';
import { FavoriteViewInformationFormComponent } from '../information-form/information-form.component';
import { FavoriteViewFiltersFormComponent } from '../filters-form/filters-form.component';
import { FavoriteViewGridFormComponent } from '../grid-form/grid-form.component';
import { FavoriteViewReportFormComponent } from '../report-form/report-form.component';

@Component({
  selector: 'iot-platform-ui-favorite-view-form',
  imports: [
    MatButtonModule,
    TranslatePipe,
    MatStepperModule,
    ReactiveFormsModule,
    FavoriteViewInformationFormComponent,
    FavoriteViewFiltersFormComponent,
    FavoriteViewGridFormComponent,
    FavoriteViewReportFormComponent,
    FavoriteViewSubscribersFormComponent
  ],
  providers: [{ provide: STEPPER_GLOBAL_OPTIONS, useValue: { showError: true } }],
  templateUrl: './favorite-view-form.component.html',
  styleUrls: ['./favorite-view-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FavoriteViewFormComponent {
  private readonly mediaWatcher: MediaWatcherService = inject(MediaWatcherService);
  private readonly featureFlagsService: FeatureFlagsService = inject(FeatureFlagsService);

  favoriteView = input<FavoriteView | undefined>();
  currentFilters = input<Filter[]>([]);
  masterView = input<string>();
  createForBusinessProfile = input<boolean>(false);
  onSaveForm = input<(device: FavoriteView) => void>();
  saveForm = output<{ favoriteView: FavoriteView; grid: I4BGrid<I4BGridOptions, I4BGridData> | null }>();
  onCancelForm = input<() => void>();
  cancelForm = output<void>();

  steps = FavoriteViewFormSteps;
  stepperOrientation = this.mediaWatcher.stepperOrientation;
  FavoriteViewReportStatus = FavoriteViewReportStatus;
  selectedGrid = signal<I4BGrid<I4BGridOptions, I4BGridData> | null>(null);

  private readonly isEventsMasterView: Signal<boolean> = computed(() => {
    const masterView = this.masterView();
    return masterView === 'asset-events' || masterView === 'device-events';
  });

  showReportExport: Signal<boolean> = computed(() =>
    this.featureFlagsService.isFeatureEnabled(
      this.isEventsMasterView() ? FeatureFlags.FavoriteViewReportExportForEvents : FeatureFlags.FavoriteViewReportExport
    )
  );

  form = new UntypedFormGroup({
    [FavoriteViewFormSteps.INFORMATION]: new UntypedFormControl(null, [Validators.required]),
    [FavoriteViewFormSteps.FILTERS]: new UntypedFormControl(null, [Validators.required]),
    [FavoriteViewFormSteps.GRID]: new UntypedFormControl(null, [Validators.required]),
    [FavoriteViewFormSteps.REPORT]: new UntypedFormControl(null),
    [FavoriteViewFormSteps.SUBSCRIBERS]: new UntypedFormControl(null)
  });

  constructor() {
    effect(() => {
      const showReportExport = this.showReportExport();
      [this.report, this.subscribers].forEach((control) => {
        if (showReportExport) {
          control?.addValidators(Validators.required);
        } else {
          control?.removeValidators(Validators.required);
        }
        control?.updateValueAndValidity();
      });
    });
  }

  formStateChanged = signal({
    [FavoriteViewFormSteps.INFORMATION]: signal(false),
    [FavoriteViewFormSteps.FILTERS]: signal(false),
    [FavoriteViewFormSteps.GRID]: signal(false),
    [FavoriteViewFormSteps.REPORT]: signal(false),
    [FavoriteViewFormSteps.SUBSCRIBERS]: signal(false)
  });

  formChanged: Signal<boolean> = computed(() => {
    const formStateChanged = this.formStateChanged();
    return (
      formStateChanged[FavoriteViewFormSteps.INFORMATION]() ||
      formStateChanged[FavoriteViewFormSteps.FILTERS]() ||
      formStateChanged[FavoriteViewFormSteps.GRID]() ||
      formStateChanged[FavoriteViewFormSteps.REPORT]() ||
      formStateChanged[FavoriteViewFormSteps.SUBSCRIBERS]()
    );
  });

  get information() {
    return this.form.get(FavoriteViewFormSteps.INFORMATION);
  }

  get filters() {
    return this.form.get(FavoriteViewFormSteps.FILTERS);
  }

  get grid() {
    return this.form.get(FavoriteViewFormSteps.GRID);
  }

  get report() {
    return this.form.get(FavoriteViewFormSteps.REPORT);
  }

  get subscribers() {
    return this.form.get(FavoriteViewFormSteps.SUBSCRIBERS);
  }

  onSave(): void {
    const masterView = this.masterView();
    const concept = this.isEventsMasterView() ? 'EVENT' : masterView?.slice(0, -1).toUpperCase();
    const information = this.information?.getRawValue();
    const filters = this.filters?.getRawValue();
    const gridId = this.grid?.getRawValue();
    const report = this.report?.getRawValue();
    const subscribers = this.subscribers?.getRawValue()?.subscribers;

    const favoriteView: FavoriteView = {
      ...(this.favoriteView()?.id ? this.favoriteView() : { concept, masterView }),
      ...information,
      filters,
      ...gridId,
      ...(this.showReportExport()
        ? {
            export: {
              ...this.report?.getRawValue(),
              cron: report.cron,
              status: report.status,
              subscribers
            }
          }
        : {})
    };

    const selectedGrid = this.selectedGrid();
    const grid = selectedGrid && selectedGrid.businessProfileId !== favoriteView.businessProfileId ? selectedGrid : null;

    this.saveForm.emit({ favoriteView, grid });
    this.onSaveForm?.()?.(favoriteView);
  }

  onCancel(): void {
    this.cancelForm.emit();
    this.onCancelForm?.()?.();
  }
}
